/* ============================================
   MAGGD — Cart drawer + Search popup behaviors
   ============================================ */

(function () {
  'use strict';

  const routes = (window.routes || {});
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';
    const cur = window.shopCurrency || {};
    // Prefer the runtime currency object, but fall back to the money format the
    // cart drawer renders server-side (data-money-format from shop.money_format).
    // The drawer is rebuilt entirely client-side from /cart.js, so without this
    // the AJAX re-render drops the currency when window.shopCurrency is missing.
    let fmt = cur.money_format; // read at call time (not cached at load)
    if (!fmt) {
      const dEl = document.querySelector('[data-cart-drawer]');
      if (dEl && dEl.dataset.moneyFormat) fmt = dEl.dataset.moneyFormat;
    }
    const dec = (cents / 100).toFixed(2);
    const amount = dec.split('.')[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + dec.split('.')[1];
    const noDec = String(Math.round(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Never fall back to "$" (wrong for EGP). If the store format is unknown,
    // prefix the ISO code when we have it, otherwise show the bare amount.
    if (!fmt) return cur.active ? cur.active + ' ' + amount : amount;
    return fmt
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', noDec)
      .replace('{{amount_with_comma_separator}}', dec.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', noDec);
  }

  // ---------- Drawer manager ----------

  // Width of the page scrollbar (root or body, whichever owns it). Used to pad
  // the body while scroll is locked, so removing the scrollbar on open doesn't
  // shift the page sideways or reveal a white gutter behind the overlay.
  function scrollbarWidth() {
    return Math.max(
      window.innerWidth - document.documentElement.clientWidth,
      window.innerWidth - document.body.clientWidth,
      0
    );
  }

  function openDrawer(drawer) {
    if (!drawer) return;
    closeAllDrawers();
    const sbw = scrollbarWidth();
    if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('maggd-drawer-open');
    const focusable = drawer.querySelector('input[type="search"], button, a, input');
    if (focusable) setTimeout(() => focusable.focus(), 50);
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('maggd-drawer-open');
    // Restore the body padding only once every drawer/popup is closed.
    if (!document.querySelector('.maggd-drawer.is-open')) document.body.style.paddingRight = '';
  }

  function closeAllDrawers() {
    document.querySelectorAll('.maggd-drawer.is-open').forEach(closeDrawer);
  }

  document.addEventListener('click', (e) => {
    const cartOpen = e.target.closest('[data-cart-open]');
    if (cartOpen) {
      const drawer = document.querySelector('[data-cart-drawer]');
      if (drawer) {
        e.preventDefault();
        openDrawer(drawer);
      }
      return;
    }
    const searchOpen = e.target.closest('[data-search-open]');
    if (searchOpen) {
      const drawer = document.querySelector('[data-search-drawer]');
      if (drawer) {
        e.preventDefault();
        openDrawer(drawer);
      }
      return;
    }
    const sizeGuideOpen = e.target.closest('[data-size-guide-open]');
    if (sizeGuideOpen) {
      const drawer = document.querySelector('[data-size-guide-drawer]');
      if (drawer) {
        e.preventDefault();
        openDrawer(drawer);
      }
      return;
    }
    const drawerClose = e.target.closest('[data-drawer-close]');
    if (drawerClose) {
      const parent = drawerClose.closest('.maggd-drawer');
      if (parent) closeDrawer(parent);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllDrawers();
  });

  // ---------- Cart ajax (client-side render from /cart.js) ----------
  //
  // The cart drawer markup lives in a section group (popups-group), and Shopify's
  // Section Rendering API does NOT render section-group sections (the `sections`
  // param / ?section_id= come back empty). So instead of server-rendering the
  // drawer, we rebuild its contents in JS from /cart.js — 100% reliable, no
  // section/group dependency. Labels are read from the first server render when
  // available, else sensible defaults.

  function pickLabel(sel, fallback) {
    const el = document.querySelector(sel);
    const t = el && (el.textContent || '').trim();
    return t || fallback;
  }
  const REMOVE_LABEL = pickLabel('[data-cart-remove]', 'Remove');
  const SUBTOTAL_LABEL = pickLabel('.maggd-drawer-subtotal span', 'Subtotal');
  const CHECKOUT_LABEL = pickLabel('.maggd-drawer-footer button[name="checkout"] span', 'Checkout');

  async function fetchCart() {
    const res = await fetch((routes.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function updateLine(line, quantity) {
    const url = (routes.cart_change_url || '/cart/change') + '.js';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    });
    if (!res.ok) throw new Error('cart change failed (' + res.status + ')');
    return res.json();
  }

  function imgUrl(u, w) {
    if (!u) return '';
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + (w || 200);
  }

  function cartItemHTML(item, line) {
    const src = item.image || (item.featured_image && item.featured_image.url) || '';
    const title = item.product_title || item.title || '';
    const img = src
      ? '<img src="' + imgUrl(src, 200) + '" alt="' + escapeHtml(title) + '" loading="lazy" width="100" height="100">'
      : '';
    const variant = (item.variant_title && item.variant_title.indexOf('Default') === -1)
      ? '<p class="maggd-cart-item-variant">' + escapeHtml(item.variant_title) + '</p>'
      : '';
    return '' +
      '<li class="maggd-cart-item" data-cart-item data-key="' + item.key + '" data-line="' + line + '">' +
        '<a class="maggd-cart-item-image" href="' + item.url + '">' + img + '</a>' +
        '<div class="maggd-cart-item-body">' +
          '<a class="maggd-cart-item-title" href="' + item.url + '">' + escapeHtml(title) + '</a>' +
          variant +
          '<p class="maggd-cart-item-price">' + formatMoney(item.final_price) + '</p>' +
          '<div class="maggd-cart-item-controls">' +
            '<div class="maggd-qty">' +
              '<button type="button" class="maggd-qty-btn" data-qty-decrease aria-label="Decrease">−</button>' +
              '<input class="maggd-qty-input" type="number" min="0" value="' + item.quantity + '" data-qty-input aria-label="Quantity">' +
              '<button type="button" class="maggd-qty-btn" data-qty-increase aria-label="Increase">+</button>' +
            '</div>' +
            '<button type="button" class="maggd-cart-item-remove" data-cart-remove aria-label="Remove">' + escapeHtml(REMOVE_LABEL) + '</button>' +
          '</div>' +
        '</div>' +
      '</li>';
  }

  function emptyHTML() {
    return '<div class="maggd-cart-empty" data-cart-empty>' +
      '<p class="maggd-cart-empty-title">Your cart is empty.</p>' +
      '<a class="btn btn-primary" href="/collections/all"><span>Shop the Collection</span><div class="btn-fill"></div></a>' +
      '</div>';
  }

  function footerHTML(cart) {
    return '<footer class="maggd-drawer-footer" data-cart-footer>' +
        '<div class="maggd-drawer-subtotal"><span>' + escapeHtml(SUBTOTAL_LABEL) + '</span>' +
        '<span data-cart-subtotal>' + formatMoney(cart.total_price) + '</span></div>' +
        '<form action="' + (routes.cart_url || '/cart') + '" method="post" novalidate>' +
        '<button type="submit" name="checkout" class="btn btn-primary btn-block">' +
        '<span>' + escapeHtml(CHECKOUT_LABEL) + '</span><div class="btn-fill"></div></button>' +
        '</form></footer>';
  }

  function updateShipping(drawer, cart) {
    const enabled = drawer.dataset.thresholdEnabled === 'true';
    const qty = parseInt(drawer.dataset.freeShipQty || '0', 10);
    if (!enabled || qty <= 0) return;
    const fill = drawer.querySelector('[data-shipping-fill]');
    const text = drawer.querySelector('[data-shipping-text]');
    const count = cart.item_count;
    const progress = Math.min(100, (count / qty) * 100);
    if (fill) fill.style.width = progress + '%';
    if (text) {
      if (count >= qty) {
        text.textContent = drawer.dataset.shipUnlocked || 'Free shipping achieved!';
      } else {
        text.textContent = drawer.dataset.shipMessage || ('Buy ' + qty + ' items to get free shipping');
      }
    }
  }

  // Rebuild the drawer contents from a Shopify /cart.js (or /cart/change.js) object.
  function renderCart(cart) {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || !cart) return drawer;

    // Count everywhere (header bubble + drawer title).
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });

    // Body: items list or empty state.
    const body = drawer.querySelector('.maggd-drawer-body');
    if (body) {
      if (cart.item_count > 0) {
        body.innerHTML = '<ul class="maggd-cart-items" data-cart-items>' +
          cart.items.map((item, i) => cartItemHTML(item, i + 1)).join('') + '</ul>';
      } else {
        body.innerHTML = emptyHTML();
      }
    }

    // Footer (checkout) — present only when there are items.
    const panel = drawer.querySelector('.maggd-drawer-panel') || drawer;
    const existingFooter = drawer.querySelector('[data-cart-footer]');
    if (cart.item_count > 0) {
      if (existingFooter) existingFooter.outerHTML = footerHTML(cart);
      else if (body) body.insertAdjacentHTML('afterend', footerHTML(cart));
      else panel.insertAdjacentHTML('beforeend', footerHTML(cart));
    } else if (existingFooter) {
      existingFooter.remove();
    }

    updateShipping(drawer, cart);
    return drawer;
  }

  function publishCart(action, cart, extra) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // internal:true marks events whose UI we already handled here, so our own
      // cart-update subscriber (which exists for *external* sources) ignores them.
      publish(PUB_SUB_EVENTS.cartUpdate, Object.assign({ action: action, cart: cart, internal: true }, extra || {}));
    }
  }

  function publishCartError(message, context) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartError, { error: message || 'cart error', context: context });
    }
  }

  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      const item = remove.closest('[data-cart-item]');
      if (!item) return;
      const line = parseInt(item.dataset.line, 10);
      item.style.opacity = '0.4';
      try {
        const cart = await updateLine(line, 0);
        renderCart(cart);
        publishCart('remove', cart, { line: line });
      } catch (err) {
        item.style.opacity = '1';
        publishCartError(err && err.message, 'remove-item');
      }
      return;
    }

    const dec = e.target.closest('[data-qty-decrease]');
    const inc = e.target.closest('[data-qty-increase]');
    if (dec || inc) {
      e.preventDefault();
      const item = (dec || inc).closest('[data-cart-item]');
      if (!item) return;
      const input = item.querySelector('[data-qty-input]');
      const line = parseInt(item.dataset.line, 10);
      let qty = parseInt(input ? input.value : '0', 10) || 0;
      qty = dec ? Math.max(0, qty - 1) : qty + 1;
      if (input) input.value = qty;
      try {
        const cart = await updateLine(line, qty);
        renderCart(cart);
        publishCart('update', cart, { line: line, quantity: qty });
      } catch (err) {
        publishCartError(err && err.message, 'update-quantity');
      }
    }
  });

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const url = (routes.cart_add_url || '/cart/add') + '.js';
    const formData = new FormData(form);
    const id = formData.get('id');
    const qty = parseInt(formData.get('quantity'), 10) || 1;
    const btn = form.querySelector('[type="submit"]');
    if (!id) return; // no variant selected — nothing to add
    if (btn) { btn.setAttribute('disabled', 'disabled'); btn.classList.add('is-loading'); }
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: qty }] })
      });
      const data = await res.json();
      if (!res.ok) throw new Error((data && (data.description || data.message)) || 'add to cart failed');

      // Rebuild the drawer from the authoritative cart, then open it.
      const cart = await fetchCart();
      const drawer = renderCart(cart);

      if (drawer) openDrawer(drawer);
      // If the add came from the quick-add modal, close it so the drawer is visible.
      if (window.quickAddModal && typeof window.quickAddModal.isOpen === 'function' && window.quickAddModal.isOpen()) {
        window.quickAddModal.close();
      }
      publishCart('add', cart);
    } catch (err) {
      publishCartError(err && err.message, 'add-to-cart');
      console.error('MAGGD: add to cart failed —', err);
    } finally {
      if (btn) { btn.removeAttribute('disabled'); btn.classList.remove('is-loading'); }
    }
  });

  // External add-to-cart sources (e.g. quick-add on product cards) publish a
  // cart-update event instead of submitting our product form. Listen for those,
  // re-render the drawer from /cart.js, and open it — keeping the pub/sub bus
  // linked to the live cart drawer.
  async function refreshDrawer() {
    try {
      const cart = await fetchCart();
      return renderCart(cart);
    } catch (err) {
      return document.querySelector('[data-cart-drawer]');
    }
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (data) => {
      if (!data || data.internal) return;          // our own mutations already updated the UI
      if (data.action && data.action !== 'add') return;
      if (window.quickAddModal && typeof window.quickAddModal.isOpen === 'function' && window.quickAddModal.isOpen()) {
        window.quickAddModal.close();
      }
      const drawer = await refreshDrawer();
      if (drawer) openDrawer(drawer);
    });
  }

  // ---------- Predictive search ----------

  const searchDrawer = document.querySelector('[data-search-drawer]');
  if (searchDrawer) {
    const input = searchDrawer.querySelector('[data-search-input]');
    const results = searchDrawer.querySelector('[data-search-results]');
    let debounce;

    input && input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(debounce);
      if (!q) {
        results.innerHTML = '<p class="maggd-search-hint" data-search-hint>' + (window.maggdTranslations && window.maggdTranslations.search_hint || 'Start typing to search the collection') + '</p>';
        return;
      }
      debounce = setTimeout(() => runPredictive(q, results), 250);
    });
  }

  async function runPredictive(query, container) {
    const base = (routes.predictive_search_url || '/search/suggest');
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor'
    });
    const url = base + '.json?' + params.toString();
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      renderPredictive(data.resources && data.resources.results, query, container);
    } catch (err) {
      container.innerHTML = '<p class="maggd-search-hint">' + (window.maggdTranslations && window.maggdTranslations.search_error || 'Search unavailable') + '</p>';
    }
  }

  function renderPredictive(results, query, container) {
    if (!results) {
      container.innerHTML = '<p class="maggd-search-hint">' + (window.maggdTranslations && window.maggdTranslations.search_no_results || 'No results') + '</p>';
      return;
    }
    let html = '';

    if (results.products && results.products.length) {
      html += '<div class="maggd-search-section"><h3 class="maggd-search-section-title">' + (window.maggdTranslations && window.maggdTranslations.search_products || 'Products') + '</h3><div class="maggd-search-products">';
      results.products.forEach((p) => {
        const img = p.featured_image && p.featured_image.url ? '<img class="maggd-search-product-image" src="' + p.featured_image.url + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '';
        html += '<a class="maggd-search-product" href="' + p.url + '">' + img + '<div><p class="maggd-search-product-title">' + escapeHtml(p.title) + '</p><p class="maggd-search-product-price">' + (p.price ? formatMoney(parseFloat(p.price) * 100) : '') + '</p></div></a>';
      });
      html += '</div></div>';
    }

    if (results.collections && results.collections.length) {
      html += '<div class="maggd-search-section"><h3 class="maggd-search-section-title">' + (window.maggdTranslations && window.maggdTranslations.search_collections || 'Collections') + '</h3><div class="maggd-search-links">';
      results.collections.forEach((c) => {
        html += '<a class="maggd-search-link" href="' + c.url + '">' + escapeHtml(c.title) + '</a>';
      });
      html += '</div></div>';
    }

    if (results.pages && results.pages.length) {
      html += '<div class="maggd-search-section"><h3 class="maggd-search-section-title">' + (window.maggdTranslations && window.maggdTranslations.search_pages || 'Pages') + '</h3><div class="maggd-search-links">';
      results.pages.forEach((p) => {
        html += '<a class="maggd-search-link" href="' + p.url + '">' + escapeHtml(p.title) + '</a>';
      });
      html += '</div></div>';
    }

    if (!html) html = '<p class="maggd-search-hint">' + (window.maggdTranslations && window.maggdTranslations.search_no_results || 'No results') + '</p>';
    container.innerHTML = html;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
})();
